/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
class SlideCart {
  constructor() {
    this.drawer = document.querySelector('[data-slide-cart]');
    this.overlay = document.querySelector('[data-slide-cart-overlay]');
    this.triggers = document.querySelectorAll('[data-slide-cart-trigger]');
    this.closeButtons = document.querySelectorAll('[data-slide-cart-close]');
    this.itemsContainer = document.querySelector('[data-slide-cart-items]');
    this.contentContainer = document.querySelector('[data-slide-cart-content]');
    this.loadingContainer = document.querySelector('[data-slide-cart-loading]');
    this.isOpen = false;
    this.cartData = null;
    this.isUpdating = false;

    this.init();
  }

  init() {
    // Initialize event listeners
    this.attachEventListeners();
    this.setupKeyboardNavigation();

    // Listen for cart updates from other sources
    document.addEventListener('cart:updated', () => {
      if (this.isOpen) {
        this.fetchCart();
      }
    });

    // Listen for add to cart events
    document.addEventListener('cart:added', () => {
      this.fetchCart().then(() => {
        this.open();
        this.showNotification('slide_cart.item_added');
      });
    });
  }

  attachEventListeners() {
    // Open drawer triggers
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', e => {
        e.preventDefault();
        this.toggle();
      });
    });

    // Close buttons
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Event delegation for cart items
    if (this.itemsContainer) {
      this.itemsContainer.addEventListener('click', e => {
        // Remove whole bundle (all lines sharing the _bundle_id)
        if (e.target.closest('[data-remove-bundle]')) {
          const button = e.target.closest('[data-remove-bundle]');
          const confirmText = window.theme?.translations?.bundle?.removeSetConfirm;
          if (confirmText && !window.confirm(confirmText)) return;
          this.removeBundle(button.dataset.removeBundle);
          return;
        }

        // Remove a server-side set: one cart line that Shopify expands into
        // components (cart transform). Same wording as removing a builder set.
        if (e.target.closest('[data-remove-set-line]')) {
          const button = e.target.closest('[data-remove-set-line]');
          const confirmText = window.theme?.translations?.bundle?.removeSetConfirm;
          if (confirmText && !window.confirm(confirmText)) return;
          this.removeItem(parseInt(button.dataset.removeSetLine, 10));
          return;
        }

        // Remove item
        if (e.target.closest('[data-remove-item]')) {
          const button = e.target.closest('[data-remove-item]');

          // Removing a component of a currently discounted set cancels the
          // set price — warn before doing so.
          const group = button.closest('[data-bundle-group]');
          if (group?.dataset.bundleDiscounted === 'true') {
            const confirmText = window.theme?.translations?.bundle?.removePartConfirm;
            if (confirmText && !window.confirm(confirmText)) return;
          }

          const line = parseInt(button.dataset.line, 10);
          this.removeItem(line);
        }

        // Decrease quantity
        if (e.target.closest('[data-decrease-quantity]')) {
          const button = e.target.closest('[data-decrease-quantity]');
          const line = parseInt(button.dataset.line, 10);
          const input = this.itemsContainer.querySelector(
            `[data-quantity-input][data-line="${line}"]`
          );
          const newQuantity = Math.max(0, parseInt(input.value, 10) - 1);
          this.updateQuantity(line, newQuantity);
        }

        // Increase quantity
        if (e.target.closest('[data-increase-quantity]')) {
          const button = e.target.closest('[data-increase-quantity]');
          const line = parseInt(button.dataset.line, 10);
          const input = this.itemsContainer.querySelector(
            `[data-quantity-input][data-line="${line}"]`
          );
          const newQuantity = parseInt(input.value, 10) + 1;
          this.updateQuantity(line, newQuantity);
        }
      });

      // Quantity input change
      this.itemsContainer.addEventListener('change', e => {
        if (e.target.matches('[data-quantity-input]')) {
          const line = parseInt(e.target.dataset.line, 10);
          const newQuantity = Math.max(0, parseInt(e.target.value, 10) || 0);
          this.updateQuantity(line, newQuantity);
        }
      });
    }

    // Cart note
    const noteTextarea = document.querySelector('[data-cart-note]');
    if (noteTextarea) {
      let noteTimeout;
      noteTextarea.addEventListener('input', e => {
        clearTimeout(noteTimeout);
        noteTimeout = setTimeout(() => {
          this.updateCartNote(e.target.value);
        }, 500);
      });
    }
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  open() {
    if (!this.drawer || !this.overlay) return;

    // Add open classes
    this.drawer.classList.remove('translate-x-full');
    this.drawer.classList.add('translate-x-0');
    this.overlay.classList.remove('opacity-0', 'pointer-events-none');
    this.overlay.classList.add('opacity-100', 'pointer-events-auto');

    // Update trigger aria
    this.triggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'true');
    });

    // Set focus to close button
    const closeButton = this.drawer.querySelector('[data-slide-cart-close]');
    if (closeButton) {
      setTimeout(() => closeButton.focus(), 100);
    }

    this.isOpen = true;

    // Fetch latest cart data
    this.fetchCart();

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.drawer || !this.overlay) return;

    // Remove open classes
    this.drawer.classList.add('translate-x-full');
    this.drawer.classList.remove('translate-x-0');
    this.overlay.classList.add('opacity-0', 'pointer-events-none');
    this.overlay.classList.remove('opacity-100', 'pointer-events-auto');

    // Update trigger aria
    this.triggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
      // Return focus to trigger
      if (
        document.activeElement &&
        this.drawer.contains(document.activeElement)
      ) {
        trigger.focus();
      }
    });

    this.isOpen = false;

    // Restore body scroll
    document.body.style.overflow = '';
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  showLoading() {
    if (this.loadingContainer && this.contentContainer) {
      this.loadingContainer.classList.remove('hidden');
      this.contentContainer.classList.add('opacity-50', 'pointer-events-none');
    }
  }

  hideLoading() {
    if (this.loadingContainer && this.contentContainer) {
      this.loadingContainer.classList.add('hidden');
      this.contentContainer.classList.remove(
        'opacity-50',
        'pointer-events-none'
      );
    }
  }

  async fetchCart() {
    try {
      const cartUrl = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}.js`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch cart');

      this.cartData = await response.json();
      await this.renderCart();
      this.updateHeaderCount();

      return this.cartData;
    } catch (error) {
      console.error('Error fetching cart:', error);
      this.showNotification('slide_cart.error', 'error');
      return null;
    }
  }

  async updateQuantity(line, quantity) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.showLoading();

    try {
      const changeUrl = window.routes?.cart_change_url || '/cart/change';
      const response = await fetch(`${changeUrl}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line,
          quantity,
        }),
      });

      if (!response.ok) throw new Error('Failed to update quantity');

      this.cartData = await response.json();
      await this.renderCart();
      this.updateHeaderCount();

      if (quantity === 0) {
        this.showNotification('slide_cart.item_removed', 'success');
      } else {
        this.showNotification('slide_cart.quantity_updated', 'success');
      }

      // Dispatch custom event
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: this.cartData })
      );
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showNotification('slide_cart.error', 'error');
    } finally {
      this.isUpdating = false;
      this.hideLoading();
    }
  }

  async removeItem(line) {
    return this.updateQuantity(line, 0);
  }

  async removeBundle(bundleId) {
    if (this.isUpdating || !this.cartData || !bundleId) return;

    const updates = {};
    this.cartData.items.forEach(item => {
      if (item.properties?._bundle_id === bundleId) {
        updates[item.key] = 0;
      }
    });
    if (Object.keys(updates).length === 0) return;

    this.isUpdating = true;
    this.showLoading();

    try {
      const updateUrl = window.routes?.cart_update_url || '/cart/update';
      const response = await fetch(`${updateUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) throw new Error('Failed to remove bundle');

      this.cartData = await response.json();
      await this.renderCart();
      this.updateHeaderCount();
      this.showNotification('slide_cart.item_removed', 'success');

      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: this.cartData })
      );
    } catch (error) {
      console.error('Error removing bundle:', error);
      this.showNotification('slide_cart.error', 'error');
    } finally {
      this.isUpdating = false;
      this.hideLoading();
    }
  }

  async updateCartNote(note) {
    try {
      const updateUrl = window.routes?.cart_update_url || '/cart/update';
      const response = await fetch(`${updateUrl}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note,
        }),
      });

      if (!response.ok) throw new Error('Failed to update note');

      this.cartData = await response.json();
    } catch (error) {
      console.error('Error updating cart note:', error);
    }
  }

  /**
   * Re-render the drawer's item list from Liquid via the Section Rendering API.
   *
   * Nothing is templated in JS on purpose: /cart.js reports a server-side
   * expanded set line only as `has_components: true` and never names its
   * components — those exist solely in Liquid as `item.item_components`.
   * Rendering sections/slide-cart.liquid keeps a single source of markup for
   * the cart page, the drawer's first paint and every update after it.
   *
   * The summary node is deliberately left in place (only its subtotal is
   * updated): the cart-note listener is bound to it directly and would not
   * survive a replacement.
   */
  async renderCart() {
    if (!this.contentContainer || !this.cartData) return;

    const root = window.Shopify?.routes?.root || '/';
    let content = null;

    try {
      const response = await fetch(`${root}?sections=slide-cart`);
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const sections = await response.json();
      const doc = new DOMParser().parseFromString(
        sections['slide-cart'] || '',
        'text/html'
      );
      content = doc.querySelector('[data-slide-cart-content]');
    } catch (error) {
      // Leave the drawer showing its last good state rather than blanking it.
      console.error('Error rendering slide cart:', error);
      return;
    }

    if (!content) return;
    this.contentContainer.innerHTML = content.innerHTML;

    const isEmpty = this.cartData.item_count === 0;
    const summary = document.querySelector('[data-slide-cart-summary]');
    const milestonesContainer = document.querySelector(
      '[data-milestones-container]'
    );

    if (isEmpty) {
      if (summary) summary.classList.add('hidden');
      if (milestonesContainer) milestonesContainer.classList.add('hidden');

      // The empty state carries its own close button.
      const closeButton = this.contentContainer.querySelector(
        '[data-slide-cart-close]'
      );
      if (closeButton) {
        closeButton.addEventListener('click', () => this.close());
      }
    } else {
      if (summary) {
        summary.classList.remove('hidden');
        this.updateSummary();
      }
      if (milestonesContainer) milestonesContainer.classList.remove('hidden');

      if (window.cartMilestones) {
        window.cartMilestones.updateProgress(this.cartData);
      }
    }

    // Update item count in header
    this.updateItemCount();
  }

  updateSummary() {
    if (!this.cartData) return;

    const subtotalElement = document.querySelector('[data-cart-subtotal]');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(this.cartData.total_price);
    }
  }

  updateItemCount() {
    const countElements = document.querySelectorAll('[data-cart-item-count]');
    countElements.forEach(element => {
      element.textContent = `(${this.cartData.item_count})`;
    });
  }

  updateHeaderCount() {
    const countElements = document.querySelectorAll('[data-cart-count]');
    countElements.forEach(element => {
      element.textContent = this.cartData.item_count;

      if (this.cartData.item_count > 0) {
        element.classList.remove('hidden');
      } else {
        element.classList.add('hidden');
      }
    });
  }

  formatMoney(cents) {
    // Format money using the active market's currency symbol.
    // EUR markets → "29,99€"; CHF (Switzerland) → "29,99 CHF".
    const value = (cents / 100).toFixed(2).replace('.', ',');
    const symbol = window.theme?.cart?.currencySymbol || '€';
    return symbol === '€' ? `${value}€` : `${value} ${symbol}`;
  }

  showNotification(messageKey, type = 'success') {
    // This could be expanded to show toast notifications
    console.log(`[${type}] ${messageKey}`);
  }
}

// Initialize slide cart when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.slideCart = new SlideCart();
  });
} else {
  window.slideCart = new SlideCart();
}

// Export for use in other scripts
window.SlideCart = SlideCart;
